//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { IContentLibraryAutocomplete } from '@apis/content-library/contentLibraryApiModel';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { BodyText } from '@components/atoms/text/Text';
import { FilterOption } from '@components/media-filters/filterSectionModels';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { CONTENT_DRAWER_LABELS_LIMIT } from '@content-drawer/filters/LabelsFilter';
import { findOptionByLabel } from '@content-drawer/filters/labelsUtils';
import { withTracking } from '@utils/withTracking';

export interface LabelsPanelProps {
  className?: string;
  isDisabled?: boolean;
  labels: IContentLibraryAutocomplete[];
  options: FilterOption<string>[];
  onCheck: (option: string) => void;
  selectedOptions: FilterOption<string>[];
}

export const LabelsPanel = ({
  className,
  isDisabled,
  labels,
  onCheck,
  selectedOptions,
}: LabelsPanelProps) => (
  <CheckboxFieldSet
    className={className}
    trackingId={ContentDrawerTrackingIds.LABELS_FIELDSET}
  >
    <Legend>Labels</Legend>
    {labels.slice(0, CONTENT_DRAWER_LABELS_LIMIT).map((label) => (
      <CheckboxSet
        key={label.value}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent toggle checkbox twice when click on label
          onCheck(label.value);
        }}
      >
        <LabeledCheckbox
          handleClick={() => {
            return; // Prevent default behaviour from checkbox component
          }}
          isChecked={
            findOptionByLabel(selectedOptions, label)?.isSelected ?? false
          }
          isDisabled={isDisabled}
          label={label.value}
          trackingId={ContentDrawerTrackingIds.LABEL_CHECKBOX}
        />
        <BodyText>{label.count}</BodyText>
      </CheckboxSet>
    ))}
  </CheckboxFieldSet>
);

const CheckboxFieldSet = withTracking(styled.fieldset`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`);

const Legend = styled.legend(
  ({ theme }) => `
    visibility: hidden;
    height: ${theme.spacings[0]};
    margin: ${theme.spacings[0]};
  `,
);

const CheckboxSet = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-item: center;
    width: 100%;
    padding: ${theme.spacings[1]} ${theme.spacings[0]};
    cursor: pointer;
`,
);
